import { Button, Result } from 'antd';
import { isRouteErrorResponse, useNavigate, useRouteError } from 'react-router-dom';

import { HomeIcon, OfflineIcon, RefreshIcon, SyncIcon, WarningIcon } from '@/components/icons';
import { IS_DEV } from '@/lib/env';
import { isReloadingForNewBuild, isStaleBuildError } from '@/lib/stale-build';

/**
 * What a page that failed to open says, in place of React Router's developer
 * screen ("Unexpected Application Error!", "Hey developer").
 *
 * Mounted inside the layout for pages, so the sidebar stays up and the way to
 * another page is still on screen, and at the root as the last resort.
 *
 * Three different failures, three different answers:
 *
 *   a new build     the tab outlived a deploy and reloading once did not
 *                   settle it; one button, Reload
 *   offline         the page has never been opened on this device, so its
 *                   code is not cached; say so, rather than call it a crash
 *   anything else   a real bug; Reload, or back to Today
 */
export function RouteError() {
  const error = useRouteError();
  const navigate = useNavigate();

  /* A reload is already on its way: drawing an error under it is a flash. */
  if (isReloadingForNewBuild()) return null;

  const reload = () => window.location.reload();
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
  const stale = isStaleBuildError(error);

  const detail =
    IS_DEV && error instanceof Error ? (
      <pre className="route-error-detail">{error.stack ?? error.message}</pre>
    ) : null;

  if (stale && offline) {
    return (
      <div className="route-error">
        <Result
          icon={<OfflineIcon />}
          title="This page needs the internet the first time"
          subTitle="It has not been opened on this device yet, so it is not saved for offline use. The pages you have already used still work. Reconnect, then reload."
          extra={
            <Button type="primary" icon={<RefreshIcon />} onClick={reload}>
              Reload
            </Button>
          }
        />
      </div>
    );
  }

  if (stale) {
    return (
      <div className="route-error">
        <Result
          icon={<SyncIcon />}
          title="The app was updated"
          subTitle="A new version came out while this tab was open. Reload to carry on - bills and stock you have already saved are not affected."
          extra={
            <Button type="primary" icon={<RefreshIcon />} onClick={reload} autoFocus>
              Reload
            </Button>
          }
        />
        {detail}
      </div>
    );
  }

  const notFound = isRouteErrorResponse(error) && error.status === 404;

  return (
    <div className="route-error">
      <Result
        icon={<WarningIcon />}
        title={notFound ? 'No such page' : 'This page could not open'}
        subTitle={
          notFound
            ? 'That address does not lead anywhere in the app.'
            : 'Something went wrong on our side, not anything you did. Reload to try again; if it keeps happening, go back to Today and tell us which page it was.'
        }
        extra={[
          <Button key="reload" type="primary" icon={<RefreshIcon />} onClick={reload}>
            Reload
          </Button>,
          <Button key="home" icon={<HomeIcon />} onClick={() => navigate('/')}>
            Go to Today
          </Button>,
        ]}
      />
      {detail}
    </div>
  );
}
